import React, { useEffect, useRef, useState } from 'react';
import CIFFA from '../assets/about-us/CIFFA_4C_no_tag-300x46.png';
import MapImage from '../assets/about-us/about-us-map.png';
import CardImage from '../assets/about-us/Courier-Delivery-Logo.jpg';

const AboutUs = () => {
  const [isDesktopVisible, setIsDesktopVisible] = useState(false);
  const [isMobileVisible, setIsMobileVisible] = useState([false, false, false]);
  const desktopRef = useRef(null);
  const mobileRefs = [useRef(null), useRef(null), useRef(null)];

  useEffect(() => {
    // Desktop observer
    const desktopObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsDesktopVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    
    if (desktopRef.current) {
      desktopObserver.observe(desktopRef.current);
    }

    // Mobile observers
    const mobileObservers = mobileRefs.map((ref, index) => {
      return new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsMobileVisible(prev => {
              const newState = [...prev];
              newState[index] = true;
              return newState;
            });
          }
        },
        { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
      );
    });

    mobileRefs.forEach((ref, index) => {
      if (ref.current) {
        mobileObservers[index].observe(ref.current);
      }
    });

    return () => {
      if (desktopRef.current) {
        desktopObserver.unobserve(desktopRef.current);
      }
      mobileRefs.forEach((ref, index) => {
        if (ref.current) {
          mobileObservers[index].unobserve(ref.current);
        }
      });
    };
  }, []);

  return (
    <>
      <style>
        {`
          @keyframes slideInLeft {
            from {
              transform: translateX(-100px);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          @keyframes slideInRight {
            from {
              transform: translateX(100px);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          @keyframes fadeInUp {
            from {
              transform: translateY(30px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          
          .animate-slide-left {
            animation: slideInLeft 0.8s ease-out forwards;
          }
          
          .animate-slide-right {
            animation: slideInRight 0.8s ease-out forwards;
          }
          
          .animate-fade-up {
            animation: fadeInUp 0.6s ease-out forwards;
          }

          .animate-ciffa-slide-in {
            animation: slideInRight 0.8s ease-out forwards;
          }
        `}
      </style>

      {/* Desktop Layout */}
      <section 
  ref={desktopRef}
  className="min-h-screen hidden md:flex flex-col md:flex-row text-[#1E3A8A] py-[2.5rem] md:py-[5rem] px-[1rem] lg:px-[6rem] xl:px-[9rem] md:gap-[2rem] xl:gap-[3rem] 2xl:px-[12rem] 2xl:gap-[5rem]"
>
  {/* Left Side - Desktop */}
  <div className="w-full md:w-1/2 lg:w-1/2 flex">
    <div
      className={`h-fit xl:min-h-[39rem] 2xl:min-h-[53.125rem] shadow-[0_0_3.75rem_rgba(255,255,255,0.2)] items-center relative bg-cover bg-center w-auto 2xl:w-[40vw] rounded-[3.125rem] 2xl:rounded-[4.375rem] flex flex-col justify-center transition-opacity duration-500 ${
        isDesktopVisible ? 'animate-slide-left' : 'opacity-0'
      }`}
      style={{ backgroundImage: `url(${MapImage})` }}
    >
      <div
        className="absolute inset-0 rounded-[3.125rem] 2xl:rounded-[4.375rem]"
        style={{
          backgroundImage: 'linear-gradient(to right, rgb(0, 1, 29), rgba(0, 12, 103, 0.8))',
          mixBlendMode: 'multiply'
        }}
      ></div>
      <div className="relative z-10 p-[4rem] text-white">
        <h2 className="font-[500] mb-[3rem] 2xl:mb-[4rem] text-2xl xl:text-4xl 2xl:text-5xl">Inside Big Blue</h2>
        <p className="leading-relaxed font-thin mb-[3rem] max-w-[32rem] 2xl:max-w-[38rem] text-sm md:text-md lg:text-lg xl:text-xl 2xl:text-3xl">
          We don't just move cargo, we move the world for businesses that can't afford to slow
          down. Big Blue Inc. is a Canadian freight partner trusted for over 15 years to deliver
          air, ocean, and inland logistics with precision, transparency, and total peace of
          mind. From real-time tracking to seamless customs and smart warehousing, we keep
          supply chains flowing and clients growing.
        </p>
        <p className="font-bold mb-[2rem] 2xl:mb-[3rem] max-w-[20rem] 2xl:max-w-[30rem] text-sm md:text-md lg:text-lg xl:text-xl 2xl:text-3xl">
          Certified. Connected. In control. That’s Big Blue.
        </p>
        <a href="/About" className="group flex items-center gap-[0.25rem] hover:cursor-pointer font-thin text-white text-sm md:text-lg 2xl:text-3xl">
          About Us
          <svg
            className="w-[0.75rem] h-[0.75rem] sm:w-[1rem] sm:h-[1rem] text-blue-400 transform transition-transform duration-300 group-hover:translate-x-1"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </a>
      </div>
    </div>
  </div>

  {/* Right Side - Desktop */}
  <div className=" lg:w-1/2 2xl:w-[40vw] flex flex-col justify-start lg:min-h-auto  ">
    <div 
      className={`relative w-full sm:h-[50vh] md:h-[62vh] lg:h-[75vh] xl:h-[62vh] 2xl:h-[73vh] 2xl:rounded-[4.375rem] rounded-[3.125rem] overflow-hidden shadow-[0_0_3.75rem_rgba(255,255,255,0.2)] transition-opacity duration-500 ${
        isDesktopVisible ? 'animate-slide-right' : 'opacity-0'
      }`}
    >
      <img
        src={CardImage}
        alt="Logistics"
        className="w-full h-full object-cover object-top rounded-[3.125rem] 2xl:rounded-[4.375rem]"
      />
    </div>
    <div 
      className={`mt-[3rem]  text-white px-[0.5rem] transition-opacity duration-500 ${
        isDesktopVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <p className="font-thin text-xs md:text-sm lg:text-xl xl:text-2xl 2xl:text-3xl">
        We are an active member of <span className="font-bold">CIFFA</span>
      </p>
      <img
        src={CIFFA}
        alt="CIFFA"
        className={`w-[50%] md:w-[40%] 2xl:w-[50%] mt-[0.25rem] md:mt-[0.5rem] transition-opacity duration-700 ${
          isDesktopVisible ? 'animate-ciffa-slide-in' : 'opacity-0'
        }`}
      />
    </div>
  </div>
</section>

      {/* Mobile Layout */}
      <section className="flex md:hidden flex-col text-[#1E3A8A] py-10 px-6 gap-4">
        {/* Map Section - Mobile */}
        <div
          ref={mobileRefs[0]}
          className={`relative bg-cover bg-center w-full rounded-3xl flex flex-col justify-center min-h-[400px] ${
            isMobileVisible[0] ? 'animate-fade-up' : 'opacity-0'
          }`}
          style={{ backgroundImage: `url(${MapImage})` }}
        >
          <div
            className="absolute inset-0 rounded-3xl"
            style={{
              backgroundImage: 'linear-gradient(to right, rgb(0, 1, 29), rgba(16, 4, 108, 0.7))',
            }}
          ></div>
          <div className="relative z-10 p-8 text-white">
            <h2 className="text-2xl font-bold mb-6">About Us</h2>
            <p className="text-sm leading-relaxed font-thin mb-6">
              We don't just move cargo, we move the world for businesses that can't afford to slow
              down. Big Blue Inc. is a Canadian freight partner trusted for over 15 years to deliver
              air, ocean, and inland logistics with precision, transparency, and total peace of
              mind. From real-time tracking to seamless customs and smart warehousing, we keep
              supply chains flowing and clients growing.
            </p>
            <p className="font-bold text-sm mb-6">
              CERTIFIED. CONNECTED. IN CONTROL. THAT'S BIG BLUE.
            </p>
            <a href="#" className="text-white hover:underline text-sm">
              About Us
            </a>
          </div>
        </div>

        {/* Image Section - Mobile */}
        <div 
          ref={mobileRefs[1]}
          className={`relative w-full h-64 rounded-3xl overflow-hidden shadow-md ${
            isMobileVisible[1] ? 'animate-fade-up' : 'opacity-0'
          }`}
        >
          <img
            src={CardImage}
            alt="Logistics"
            className="w-full h-full object-cover rounded-3xl"
          />
        </div>

        {/* CIFFA Section - Mobile */}
        <div 
          ref={mobileRefs[2]}
          className={`mt-2 text-white px-2 text-center w-full flex flex-col items-center ${
            isMobileVisible[2] ? 'animate-fade-up' : 'opacity-0'
          }`}
        >
          <p className="text-sm font-thin">
            We are an active member of <span className="font-bold">CIFFA</span>
          </p>
          <img
            src={CIFFA}
            alt="CIFFA"
            className={`w-3/5 mt-2 transition-opacity duration-700 ${
              isMobileVisible[2] ? 'animate-ciffa-slide-in' : 'opacity-0'
            }`}
          />

        </div>
      </section>
    </>
  );
};

export default AboutUs;